import {styled} from '@mui/material/styles'
import {
  Box,
  Paper,
  Button,
  Typography,
  Alert,
  IconButton,
} from '@mui/material'

const palette = {
  primary: '#1565c0',
  primaryLight: '#e3f2fd',
  primaryBorder: '#90caf9',
  success: '#2e7d32',
  successLight: '#e8f5e9',
  purple: '#7b1fa2',
  purpleLight: '#f3e5f5',
  orange: '#e65100',
  orangeLight: '#fff3e0',
  error: '#c62828',
  errorLight: '#ffebee',
  grey50: '#fafafa',
  grey100: '#f5f5f5',
  grey200: '#eeeeee',
  grey300: '#e0e0e0',
  grey600: '#757575',
  grey800: '#424242',
  white: '#ffffff',
}

// ─── Page Layout ────────────────────────────────────────────────────────────
export const PageWrapper = styled(Box)(({theme}) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(2.5),
}))

// ─── Filter Card ────────────────────────────────────────────────────────────
export const FilterCard = styled(Paper)({
  borderRadius: 16,
  border: `1px solid ${palette.grey200}`,
  boxShadow: 'none',
  overflow: 'hidden',
})

export const FilterHeader = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.25),
  padding: theme.spacing(1.5, 2.5),
  borderBottom: `1px solid ${palette.grey200}`,
  backgroundColor: palette.grey50,
}))

export const FilterIconBox = styled(Box)({
  width: 32,
  height: 32,
  borderRadius: 8,
  backgroundColor: palette.primaryLight,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: palette.primary,
  flexShrink: 0,
})

export const FilterTitle = styled(Typography)({
  fontWeight: 700,
  fontSize: '0.85rem',
  color: palette.grey800,
})

export const FilterBody = styled(Box)(({theme}) => ({
  padding: theme.spacing(2, 2.5),
  '& form': {
    width: '100%',
  },
}))

export const FilterHelper = styled(Typography)({
  fontSize: '0.65rem',
  color: palette.grey600,
  marginTop: 3,
})

export const RunBtn = styled(Button)(({theme}) => ({
  borderRadius: 8,
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.8rem',
  padding: theme.spacing(0.85, 2.5),
  boxShadow: 'none',
  whiteSpace: 'nowrap',
  '&:hover': {boxShadow: 'none'},
}))

export const DownloadBtn = styled(Button)(({theme}) => ({
  borderRadius: 8,
  textTransform: 'none',
  fontWeight: 600,
  fontSize: '0.75rem',
  padding: theme.spacing(0.75, 2),
  boxShadow: 'none',
  whiteSpace: 'nowrap',
}))

// ─── KPI Row ────────────────────────────────────────────────────────────────
export const KpiGrid = styled(Box)(({theme}) => ({
  display: 'grid',
  gridTemplateColumns: 'repeat(5, 1fr)',
  gap: theme.spacing(2),
  [theme.breakpoints.down('lg')]: {
    gridTemplateColumns: 'repeat(3, 1fr)',
  },
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: 'repeat(2, 1fr)',
  },
}))

export const KpiCard = styled(Paper)({
  borderRadius: 12,
  border: `1px solid ${palette.grey200}`,
  boxShadow: 'none',
  padding: 16,
  display: 'flex',
  flexDirection: 'column',
  gap: 4,
})

export const KpiTop = styled(Box)({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
})

export const KpiLabel = styled(Typography)({
  fontSize: '0.68rem',
  fontWeight: 600,
  color: palette.grey600,
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
})

export const KpiIcon = styled(Box, {
  shouldForwardProp: (p) => p !== 'bg',
})(({bg}) => ({
  width: 28,
  height: 28,
  borderRadius: 7,
  backgroundColor: bg || palette.grey100,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
}))

export const KpiValue = styled(Typography)({
  fontWeight: 800,
  fontSize: '1.4rem',
  color: palette.grey800,
  lineHeight: 1.2,
})

export const KpiSub = styled(Typography)({
  fontSize: '0.66rem',
  color: palette.grey600,
  lineHeight: 1.3,
})

export const KpiInfoBtn = styled(IconButton)({
  width: 18,
  height: 18,
  color: palette.grey300,
  '&:hover': {color: palette.grey600, backgroundColor: 'transparent'},
})

// ─── Split Indicator (PACE vs Manual) ───────────────────────────────────────
export const SplitCard = styled(Paper)({
  borderRadius: 16,
  border: `1px solid ${palette.grey200}`,
  boxShadow: 'none',
  overflow: 'hidden',
})

export const SplitHeader = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2, 2.5),
  borderBottom: `1px solid ${palette.grey200}`,
}))

export const SplitTitle = styled(Typography)({
  fontWeight: 700,
  fontSize: '0.9rem',
  color: palette.grey800,
})

export const SplitBody = styled(Box)(({theme}) => ({
  padding: theme.spacing(2.5),
}))

export const SplitBarTrack = styled(Box)({
  height: 28,
  borderRadius: 6,
  overflow: 'hidden',
  display: 'flex',
  width: '100%',
  backgroundColor: palette.grey100,
})

export const SplitBarSegment = styled(Box, {
  shouldForwardProp: (p) => p !== 'pct' && p !== 'bg',
})(({pct, bg}) => ({
  width: `${pct}%`,
  backgroundColor: bg,
  transition: 'width 0.5s ease',
  minWidth: pct > 0 ? 2 : 0,
}))

export const SplitLegend = styled(Box)(({theme}) => ({
  display: 'flex',
  gap: theme.spacing(3),
  marginTop: theme.spacing(1.5),
  flexWrap: 'wrap',
}))

export const LegendItem = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}))

export const LegendDot = styled(Box, {
  shouldForwardProp: (p) => p !== 'bg',
})(({bg}) => ({
  width: 10,
  height: 10,
  borderRadius: 3,
  backgroundColor: bg,
  flexShrink: 0,
}))

export const LegendLabel = styled(Typography)({
  fontSize: '0.72rem',
  color: palette.grey600,
  fontWeight: 500,
})

export const LegendValue = styled(Typography)({
  fontSize: '0.72rem',
  fontWeight: 700,
  color: palette.grey800,
})

// ─── PACE Breakdown Chips ───────────────────────────────────────────────────
export const BreakdownRow = styled(Box)(({theme}) => ({
  display: 'flex',
  gap: theme.spacing(1.5),
  marginTop: theme.spacing(2),
  flexWrap: 'wrap',
}))

export const BreakdownChip = styled(Box, {
  shouldForwardProp: (p) => p !== 'bg' && p !== 'color',
})(({bg, color: textColor}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: 6,
  padding: '6px 14px',
  borderRadius: 8,
  backgroundColor: bg,
  border: `1px solid ${textColor}20`,
}))

export const BreakdownChipLabel = styled(Typography, {
  shouldForwardProp: (p) => p !== 'chipColor',
})(({chipColor}) => ({
  fontSize: '0.7rem',
  fontWeight: 600,
  color: chipColor,
}))

export const BreakdownChipCount = styled(Typography, {
  shouldForwardProp: (p) => p !== 'chipColor',
})(({chipColor}) => ({
  fontSize: '0.85rem',
  fontWeight: 800,
  color: chipColor,
}))

// ─── Section Cards (Merchant / Courier tables) ─────────────────────────────
export const SectionCard = styled(Paper)({
  borderRadius: 16,
  border: `1px solid ${palette.grey200}`,
  boxShadow: 'none',
  overflow: 'hidden',
})

export const SectionHeader = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(2, 2.5),
  borderBottom: `1px solid ${palette.grey200}`,
  backgroundColor: palette.grey50,
}))

export const SectionHeaderLeft = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1),
}))

export const SectionTitle = styled(Typography)({
  fontWeight: 700,
  fontSize: '0.88rem',
  color: palette.grey800,
})

export const SectionBadge = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  padding: '2px 8px',
  borderRadius: 4,
  fontSize: '0.65rem',
  fontWeight: 700,
  backgroundColor: palette.primaryLight,
  color: palette.primary,
})

// ─── Custom Table ───────────────────────────────────────────────────────────
export const StyledTable = styled('table')({
  width: '100%',
  borderCollapse: 'separate',
  borderSpacing: 0,
})

export const StyledTh = styled('th', {
  shouldForwardProp: (p) => p !== 'align',
})(({align}) => ({
  padding: '10px 16px',
  fontSize: '0.68rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: palette.grey600,
  backgroundColor: palette.grey50,
  borderBottom: `2px solid ${palette.grey200}`,
  textAlign: align || 'left',
  whiteSpace: 'nowrap',
}))

export const StyledTr = styled('tr', {
  shouldForwardProp: (p) => p !== 'isLast',
})(({isLast}) => ({
  transition: 'background-color 0.1s ease',
  '&:hover': {backgroundColor: '#f5f9ff'},
  '& td': {borderBottom: isLast ? 'none' : `1px solid ${palette.grey200}`},
}))

export const StyledTd = styled('td', {
  shouldForwardProp: (p) => p !== 'align' && p !== 'bold',
})(({align, bold}) => ({
  padding: '12px 16px',
  fontSize: '0.8rem',
  color: palette.grey800,
  textAlign: align || 'left',
  verticalAlign: 'middle',
  ...(bold && {fontWeight: 700}),
}))

export const CellBar = styled(Box, {
  shouldForwardProp: (p) => p !== 'pct' && p !== 'barColor',
})(({pct, barColor}) => ({
  height: 4,
  borderRadius: 2,
  backgroundColor: palette.grey100,
  width: '100%',
  maxWidth: 80,
  display: 'inline-block',
  position: 'relative',
  overflow: 'hidden',
  verticalAlign: 'middle',
  marginLeft: 8,
  '&::after': {
    content: '""',
    position: 'absolute',
    left: 0,
    top: 0,
    height: '100%',
    width: `${Math.min(pct || 0, 100)}%`,
    borderRadius: 2,
    backgroundColor: barColor || palette.primary,
  },
}))

export const PctChip = styled(Box, {
  shouldForwardProp: (p) => p !== 'chipBg' && p !== 'chipColor',
})(({chipBg, chipColor}) => ({
  display: 'inline-flex',
  padding: '1px 7px',
  borderRadius: 4,
  fontSize: '0.65rem',
  fontWeight: 700,
  backgroundColor: chipBg || palette.grey100,
  color: chipColor || palette.grey600,
  marginLeft: 6,
}))

export const TypeBadge = styled(Box, {
  shouldForwardProp: (p) => p !== 'bg' && p !== 'textColor',
})(({bg, textColor}) => ({
  display: 'inline-flex',
  padding: '2px 8px',
  borderRadius: 4,
  fontSize: '0.65rem',
  fontWeight: 700,
  backgroundColor: bg,
  color: textColor,
  textTransform: 'uppercase',
  letterSpacing: '0.03em',
}))

// ─── Orders Table ───────────────────────────────────────────────────────────
export const OrdersTableCard = styled(Paper)({
  borderRadius: 16,
  border: `1px solid ${palette.grey200}`,
  boxShadow: 'none',
  overflow: 'hidden',
})

export const PaginationRow = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: theme.spacing(1.5, 2.5),
  borderTop: `1px solid ${palette.grey200}`,
}))

export const PaginationInfo = styled(Typography)({
  fontSize: '0.72rem',
  color: palette.grey600,
  fontWeight: 500,
})

export const PaginationBtns = styled(Box)(({theme}) => ({
  display: 'flex',
  gap: theme.spacing(0.5),
}))

// ─── Data Loaded Progress Bar ────────────────────────────────────────────────
export const DataLoadedCard = styled(Paper)({
  borderRadius: 12,
  border: `1px solid ${palette.grey200}`,
  boxShadow: 'none',
  padding: '14px 20px',
  display: 'flex',
  alignItems: 'center',
  gap: 16,
})

export const DataLoadedInfo = styled(Box)({
  flex: 1,
  minWidth: 0,
})

export const DataLoadedText = styled(Typography)({
  fontSize: '0.78rem',
  fontWeight: 600,
  color: palette.grey800,
})

export const DataLoadedSub = styled(Typography)({
  fontSize: '0.68rem',
  color: palette.grey600,
  marginTop: 2,
})

export const DataLoadedBarTrack = styled(Box)({
  height: 6,
  borderRadius: 3,
  backgroundColor: palette.grey100,
  width: '100%',
  marginTop: 8,
  position: 'relative',
  overflow: 'hidden',
})

export const DataLoadedBarFill = styled(Box, {
  shouldForwardProp: (p) => p !== 'pct',
})(({pct}) => ({
  position: 'absolute',
  left: 0,
  top: 0,
  height: '100%',
  width: `${Math.min(pct || 0, 100)}%`,
  borderRadius: 3,
  backgroundColor: '#1565c0',
  transition: 'width 0.4s ease',
}))

export const DataLoadedPct = styled(Typography)({
  fontSize: '0.85rem',
  fontWeight: 800,
  color: palette.primary,
  whiteSpace: 'nowrap',
})

// ─── Visual Breakdown Bars ──────────────────────────────────────────────────
export const VisualBarRow = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: '6px 0',
}))

export const VisualBarName = styled(Typography)({
  fontSize: '0.72rem',
  fontWeight: 600,
  color: palette.grey800,
  width: 140,
  flexShrink: 0,
  overflow: 'hidden',
  textOverflow: 'ellipsis',
  whiteSpace: 'nowrap',
})

export const VisualBarTrack = styled(Box)({
  flex: 1,
  height: 20,
  borderRadius: 4,
  backgroundColor: palette.grey100,
  overflow: 'hidden',
  display: 'flex',
  position: 'relative',
})

export const VisualBarFill = styled(Box, {
  shouldForwardProp: (p) => p !== 'pct' && p !== 'bg',
})(({pct, bg}) => ({
  height: '100%',
  width: `${Math.min(pct || 0, 100)}%`,
  backgroundColor: bg,
  transition: 'width 0.4s ease',
  minWidth: pct > 0 ? 2 : 0,
}))

export const VisualBarValue = styled(Typography)({
  fontSize: '0.68rem',
  fontWeight: 700,
  color: palette.grey800,
  width: 50,
  textAlign: 'right',
  flexShrink: 0,
})

// ─── Empty / Loading ────────────────────────────────────────────────────────
export const EmptyState = styled(Box)(({theme}) => ({
  padding: theme.spacing(6),
  textAlign: 'center',
}))

export const EmptyText = styled(Typography)({
  fontSize: '0.88rem',
  color: palette.grey600,
})

export const LoadingOverlay = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: 48,
})

// ─── Welcome / Empty State ──────────────────────────────────────────────────
export const WelcomeCard = styled(Paper)({
  borderRadius: 16,
  border: `1px solid ${palette.grey200}`,
  boxShadow: 'none',
  padding: '48px 32px',
  textAlign: 'center',
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
  gap: 12,
})

export const WelcomeIconBox = styled(Box)({
  width: 64,
  height: 64,
  borderRadius: 16,
  backgroundColor: palette.primaryLight,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  color: palette.primary,
  marginBottom: 8,
})

export const WelcomeTitle = styled(Typography)({
  fontWeight: 700,
  fontSize: '1.1rem',
  color: palette.grey800,
})

export const WelcomeSub = styled(Typography)({
  fontSize: '0.82rem',
  color: palette.grey600,
  maxWidth: 420,
  lineHeight: 1.6,
})

// ─── Orders Filter Bar ──────────────────────────────────────────────────────
export const OrdersFilterBar = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
  padding: theme.spacing(1.5, 2.5),
  borderBottom: `1px solid ${palette.grey200}`,
  flexWrap: 'wrap',
}))

export const SearchInput = styled('input')({
  border: `1px solid ${palette.grey300}`,
  borderRadius: 8,
  padding: '6px 12px',
  fontSize: '0.78rem',
  outline: 'none',
  width: 220,
  color: palette.grey800,
  backgroundColor: palette.white,
  transition: 'border-color 0.15s',
  '&:focus': {
    borderColor: palette.primary,
  },
  '&::placeholder': {
    color: palette.grey600,
  },
})

export const FilterChipBtn = styled(Box, {
  shouldForwardProp: (p) => p !== 'active',
})(({active}) => ({
  display: 'inline-flex',
  alignItems: 'center',
  padding: '4px 12px',
  borderRadius: 6,
  fontSize: '0.68rem',
  fontWeight: 600,
  cursor: 'pointer',
  transition: 'all 0.15s',
  backgroundColor: active ? palette.primaryLight : palette.grey100,
  color: active ? palette.primary : palette.grey600,
  border: `1px solid ${active ? palette.primaryBorder : 'transparent'}`,
  '&:hover': {
    backgroundColor: active ? palette.primaryLight : palette.grey200,
  },
}))

// ─── Sortable Table Header ──────────────────────────────────────────────────
export const SortTh = styled('th', {
  shouldForwardProp: (p) => p !== 'align' && p !== 'active',
})(({align, active}) => ({
  padding: '10px 16px',
  fontSize: '0.68rem',
  fontWeight: 700,
  textTransform: 'uppercase',
  letterSpacing: '0.04em',
  color: active ? palette.primary : palette.grey600,
  backgroundColor: palette.grey50,
  borderBottom: `2px solid ${active ? palette.primaryBorder : palette.grey200}`,
  textAlign: align || 'left',
  whiteSpace: 'nowrap',
  cursor: 'pointer',
  userSelect: 'none',
  transition: 'color 0.15s',
  '&:hover': {
    color: palette.primary,
  },
}))

// ─── Partial Data Badge ─────────────────────────────────────────────────────
export const PartialBadge = styled(Box)({
  display: 'inline-flex',
  alignItems: 'center',
  gap: 4,
  padding: '2px 8px',
  borderRadius: 4,
  fontSize: '0.6rem',
  fontWeight: 600,
  backgroundColor: palette.orangeLight,
  color: palette.orange,
})

// ─── Date Chart ─────────────────────────────────────────────────────────────
export const DateChartCard = styled(Paper)({
  borderRadius: 16,
  border: `1px solid ${palette.grey200}`,
  boxShadow: 'none',
  overflow: 'hidden',
})

export const DayBarContainer = styled(Box)(({theme}) => ({
  display: 'flex',
  flexDirection: 'column',
  gap: theme.spacing(1),
  padding: theme.spacing(2.5),
}))

export const DayBarRow = styled(Box)(({theme}) => ({
  display: 'flex',
  alignItems: 'center',
  gap: theme.spacing(1.5),
}))

export const DayLabel = styled(Typography)({
  fontSize: '0.72rem',
  fontWeight: 600,
  color: palette.grey800,
  width: 80,
  flexShrink: 0,
})

export const DayBarTrack = styled(Box)({
  flex: 1,
  height: 28,
  borderRadius: 6,
  backgroundColor: palette.grey100,
  overflow: 'hidden',
  display: 'flex',
  position: 'relative',
})

export const DayBarFill = styled(Box, {
  shouldForwardProp: (p) => p !== 'pct' && p !== 'bg',
})(({pct, bg}) => ({
  height: '100%',
  width: `${Math.min(pct || 0, 100)}%`,
  backgroundColor: bg,
  transition: 'width 0.4s ease',
  minWidth: pct > 0 ? 2 : 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}))

export const DayBarLabel = styled(Typography)({
  fontSize: '0.6rem',
  fontWeight: 700,
  color: palette.white,
  whiteSpace: 'nowrap',
})

export const DayTotal = styled(Typography)({
  fontSize: '0.72rem',
  fontWeight: 700,
  color: palette.grey800,
  width: 60,
  textAlign: 'right',
  flexShrink: 0,
})
